import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { PageHeader } from "@/components/portal/PortalLayout";
import { Button, Card, ErrorNote, Field, Note, inputClass } from "@/components/portal/Ui";

const PLATFORMS = ["Instagram", "XiaoHongShu", "TikTok"];

const BILLING_FIELDS = [
  { key: "full_name", label: "Full name (as per IC)" },
  { key: "ic_number", label: "IC number" },
  { key: "address", label: "Address" },
  { key: "phone", label: "Phone number" },
  { key: "bank_name", label: "Bank name" },
  { key: "bank_account", label: "Bank account number" },
];

/** The payee details printed on invoices the KOL generates. Never shown to clients. */
function BillingCard() {
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/account/billing")
      .then((r) => setBilling(r.data))
      .catch((e) => {
        setError(apiError(e));
        setBilling({});
      });
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {};
      BILLING_FIELDS.forEach((f) => {
        payload[f.key] = String(billing?.[f.key] || "").trim();
      });
      const { data } = await api.put("/account/billing", payload);
      setBilling(data);
      toast.success("Billing details saved");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const missing = BILLING_FIELDS.filter((f) => !String(billing?.[f.key] || "").trim());

  return (
    <Card testId="account-billing-card">
      <h2 className="text-lg font-extrabold">Billing details</h2>
      <p className="mt-1.5 text-sm body-text">
        Used to fill in the invoices you generate from My Invoices. Only you and the AnyTribe team
        can see these — they never reach clients or reports.
      </p>
      <form onSubmit={save} className="mt-5 space-y-4">
        <ErrorNote message={error} />
        {billing && missing.length > 0 && (
          <Note tone="orange" testId="billing-missing-note">
            Still needed before you can generate an invoice:{" "}
            {missing.map((f) => f.label.toLowerCase()).join(", ")}.
          </Note>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {BILLING_FIELDS.map((field) => (
            <Field key={field.key} label={field.label}>
              <input
                value={billing?.[field.key] || ""}
                onChange={(e) => setBilling((prev) => ({ ...prev, [field.key]: e.target.value }))}
                className={inputClass}
                data-testid={`account-billing-${field.key}`}
              />
            </Field>
          ))}
        </div>
        <Button type="submit" busy={busy} testId="account-billing-save">
          Save billing details
        </Button>
      </form>
    </Card>
  );
}

export default function Account() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [socials, setSocials] = useState(user?.socials?.length ? user.socials : []);
  const [waConsent, setWaConsent] = useState(Boolean(user?.whatsapp_consent));
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const isCreator = user?.role === "creator";

  const saveProfile = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    setProfileError("");
    try {
      const { data } = await api.patch("/account", {
        name: name.trim(),
        phone: phone.trim() || null,
        socials: socials.filter((s) => s.platform && s.handle.trim()),
        whatsapp_consent: isCreator ? waConsent : undefined,
      });
      setUser(data);
      toast.success("Account updated");
    } catch (e) {
      setProfileError(apiError(e));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (next.length < 8) {
      setPasswordError("Use at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setPasswordError("Both new password fields must match.");
      return;
    }
    setSavingPassword(true);
    setPasswordError("");
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setCurrent("");
      setNext("");
      setConfirm("");
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated");
    } catch (e) {
      setPasswordError(apiError(e));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div data-testid="account-page">
      <PageHeader title="Account" description="Your AnyTribe sign-in details and contact information." />

      {user?.must_change_password && (
        <div className="mb-5">
          <Note tone="orange" testId="must-change-password-note">
            You're still using the password you were set up with. Please change it below.
          </Note>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Card testId="account-profile-card">
          <h2 className="text-lg font-extrabold">Profile</h2>
          <form onSubmit={saveProfile} className="mt-5 space-y-4">
            <ErrorNote message={profileError} />
            <Field label="Email address" hint="Your email is managed by AnyTribe and cannot be changed here.">
              <input value={user?.email || ""} disabled className={`${inputClass} bg-[#F7F4EF]`} />
            </Field>
            <Field label="Role">
              <input
                value={{ admin: "Administrator", internal: "Internal team", creator: "Creator", client: "Client" }[user?.role] || ""}
                disabled
                className={`${inputClass} bg-[#F7F4EF]`}
                data-testid="account-role-input"
              />
            </Field>
            {user?.client_org_name && (
              <Field label="Client organisation">
                <input value={user.client_org_name} disabled className={`${inputClass} bg-[#F7F4EF]`} />
              </Field>
            )}
            <Field label="Display name" required>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
                data-testid="account-name-input"
              />
            </Field>
            <Field label="Phone number (optional)">
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
                data-testid="account-phone-input"
              />
            </Field>

            {isCreator && (
              <div>
                <span className="mb-1.5 block text-sm font-semibold">Social platforms</span>
                <div className="space-y-2.5">
                  {socials.map((social, index) => (
                    <div key={index} className="flex gap-2.5">
                      <select
                        value={social.platform}
                        onChange={(e) =>
                          setSocials((list) =>
                            list.map((s, i) => (i === index ? { ...s, platform: e.target.value } : s))
                          )
                        }
                        className={inputClass}
                        data-testid={`account-social-platform-${index}`}
                      >
                        {PLATFORMS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                      <input
                        value={social.handle}
                        placeholder="@handle"
                        onChange={(e) =>
                          setSocials((list) =>
                            list.map((s, i) => (i === index ? { ...s, handle: e.target.value } : s))
                          )
                        }
                        className={inputClass}
                        data-testid={`account-social-handle-${index}`}
                      />
                      <Button
                        variant="outline"
                        onClick={() => setSocials((list) => list.filter((_, i) => i !== index))}
                        testId={`account-social-remove-${index}`}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="mt-2.5"
                  testId="account-social-add"
                  onClick={() => setSocials((list) => [...list, { platform: "Instagram", handle: "" }])}
                >
                  Add platform
                </Button>
              </div>
            )}

            {isCreator && (
              <label className="flex items-start gap-2.5 rounded-xl bg-[#F7F4EF] p-3.5">
                <input
                  type="checkbox"
                  checked={waConsent}
                  onChange={(e) => setWaConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#F2811D]"
                  data-testid="account-whatsapp-consent"
                />
                <span className="text-sm leading-relaxed body-text">
                  AnyTribe may message me on WhatsApp about campaigns I'm on and payment
                  confirmations. Untick this and we'll stop — you'll still see everything in this
                  portal.
                </span>
              </label>
            )}
            <Button type="submit" busy={savingProfile} testId="account-save-button">
              Save changes
            </Button>
          </form>
        </Card>

        <Card testId="account-password-card">
          <h2 className="text-lg font-extrabold">Change password</h2>
          <form onSubmit={savePassword} className="mt-5 space-y-4">
            <ErrorNote message={passwordError} />
            <Field label="Current password" required>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className={inputClass}
                data-testid="password-current-input"
              />
            </Field>
            <Field label="New password" required hint="At least 8 characters.">
              <input
                type="password"
                required
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className={inputClass}
                data-testid="password-new-input"
              />
            </Field>
            <Field label="Confirm new password" required>
              <input
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={inputClass}
                data-testid="password-confirm-input"
              />
            </Field>
            <Button type="submit" busy={savingPassword} testId="password-save-button">
              Update password
            </Button>
          </form>
        </Card>

        {isCreator && <BillingCard />}
      </div>
    </div>
  );
}
